import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import type { ButtonProps } from "clutch/src/Button/Button.jsx";
import { Button } from "clutch/src/Button/Button.jsx";
import { LoadingSpinner } from "clutch/src/LoadingSpinner/LoadingSpinner.jsx";
import type { TFunction } from "i18next";

import { CARD_BRAND_INFO, PAYPAL_INFO } from "@/feature-wallet/constants.mjs";
import type { PaymentMethodType } from "@/feature-wallet/models/any-payment-method.mjs";
import type { CardBrand } from "@/feature-wallet/models/card-brand.mjs";
import Close from "@/inline-assets/close.svg";

const Styled = {
  Submit: styled("div")`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: var(--sp-3);

    & .loading-wrapper {
      height: var(--sp-15);

      & [role="alert"] {
        position: relative;
        display: block;

        min-height: 0;
        max-height: var(--sp-15);
      }

      svg {
        width: 4rem;
        height: 4rem;
      }
    }

    & .icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--sp-12);
      height: var(--sp-12);
      border-radius: 50%;
      border: 2px solid var(--red);
      svg {
        width: var(--sp-4_5);
        height: var(--sp-4_5);
      }
    }

    .error {
      color: var(--red);

      text-align: center;
    }
  `,
};

type PaymentTypeIconProps = {
  as?: keyof JSX.IntrinsicElements;
  className?: string;

  type: PaymentMethodType | null;
  cardBrand?: CardBrand;
} & (
  | { type: "card"; cardBrand: CardBrand }
  | { type: "paypal_account"; cardBrand?: unknown }
  | { type: "unknown"; cardBrand?: unknown }
);

export const PaymentTypeIcon = ({
  as: Ele = "span",
  type,
  cardBrand,
  className,
}: PaymentTypeIconProps) => {
  const { localization, Icon } =
    (type === "card"
      ? CARD_BRAND_INFO[cardBrand]
      : type === "paypal_account"
        ? PAYPAL_INFO
        : null) ?? CARD_BRAND_INFO["unknown"];

  const { t } = useTranslation();

  return (
    <Ele className={className}>
      <span className="visually-hidden">{t(...localization)}</span>
      <Icon />
    </Ele>
  );
};

function SubmitLoading() {
  const { t } = useTranslation();

  return (
    <Styled.Submit>
      <div className="loading-wrapper">
        <LoadingSpinner />
      </div>
      <p className="type-form--button">
        {t("common:wallet.processing", "Processing")}
      </p>
    </Styled.Submit>
  );
}

function SubmitError({
  error,
  ...props
}: {
  error?: string;
} & ButtonProps<"button">) {
  const { t } = useTranslation();

  return (
    <>
      <Styled.Submit>
        <div className="icon-wrapper">
          <Close />
        </div>
        <p className="type-form--button error">
          {error ?? t("common:wallet.transactionFailed", "Transaction failed")}
        </p>
      </Styled.Submit>
      <Button emphasis="high" size="large" type="submit" {...props}>
        {t("common:wallet.tryAgain", "Try Again")}
      </Button>
    </>
  );
}

export const FormSubmit = ({ children, ...props }) => (
  <Button type="submit" emphasis="high" {...props}>
    {children}
  </Button>
);
FormSubmit.Loading = SubmitLoading;
FormSubmit.Error = SubmitError;

export function formActions(
  t: TFunction,
  { isSubmitting, canSubmit, submitError },
) {
  if (isSubmitting) return <FormSubmit.Loading />;
  if (submitError) return <FormSubmit.Error disabled={!canSubmit} />;
  return (
    <FormSubmit disabled={!canSubmit}>
      {t("common:wallet.addCard", "Add card")}
    </FormSubmit>
  );
}
